import { YeneShopLogo } from '@shared/ui/YeneShopLogo/YeneShopLogo';
import { NotificationBell } from '@features/notifications/NotificationBell';
import { Moon, Sun, Storefront, UserCircle } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { haptics } from '@shared/lib/telegram';
import { isMiniAppLayout } from '@shared/lib/miniAppLayout';
import { useGetCategoriesQuery, useGetProductsQuery } from '@entities/product';
import { useGetMeQuery } from '@entities/user';
import { SearchField } from '@shared/ui/SearchField';
import { ButtonLink } from '@shared/ui/Button';
import { CategoryTabs } from '@widgets/CategoryTabs/CategoryTabs';
import { getThemeMode, setThemeMode, type ThemeMode } from '@shared/lib/theme';
import { EXCLUSIVE_CAMPAIGN_ENDS_AT } from '@shared/config/exclusiveCampaign';
import { MeskelBrand } from './MeskelBrand';
import styles from './TopAppBar.module.css';

function titleForPath(pathname: string): string {
  if (pathname === '/') return 'YeneShop';
  if (pathname.startsWith('/products/orders/')) return 'Deliver order';
  if (pathname.startsWith('/product/')) return 'Product';
  if (pathname.startsWith('/panel/analytics')) return 'Money analytics';
  if (pathname.startsWith('/panel/users/')) return 'Customer';
  if (pathname.startsWith('/panel/resellers')) return 'Resellers';
  if (pathname.startsWith('/seller')) return 'My shop';
  if (pathname.startsWith('/wallet')) return 'Wallet';
  if (pathname.startsWith('/orders')) return 'Orders';
  if (pathname.startsWith('/discover')) return 'Discover';
  if (pathname.startsWith('/notifications')) return 'Notifications';
  if (pathname.startsWith('/account')) return 'Account';
  if (pathname.startsWith('/reseller')) return 'Reseller';
  if (pathname.startsWith('/products')) return 'Products';
  if (pathname.startsWith('/panel')) return 'Panel';
  return 'YeneShop';
}

/** Persistent app bar shared by every route rendered above the bottom bar. */
export function TopAppBar() {
  const miniApp = isMiniAppLayout();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { data: user } = useGetMeQuery();
  const { data: categories = [] } = useGetCategoriesQuery();
  const [search, setSearch] = useState('');
  const lastScroll = useRef(0);
  const rippleId = useRef(0);
  const [hidden, setHidden] = useState(false);
  const [theme, setTheme] = useState<ThemeMode>(() => getThemeMode());
  const [meskelActive, setMeskelActive] = useState(
    () => Date.now() < Date.parse(EXCLUSIVE_CAMPAIGN_ENDS_AT),
  );
  const [ripple, setRipple] = useState<{ x: number; y: number; id: number } | null>(null);
  const isDark =
    theme === 'dark' ||
    (theme === 'telegram' && document.documentElement.dataset.theme === 'dark');
  const title = titleForPath(pathname);
  const storefront = title === 'YeneShop';
  const catalogRoute = pathname === '/' || (storefront && /^\/[^/]+\/?$/.test(pathname) && !['/login', '/invite'].includes(pathname));
  const searchValue = catalogRoute ? params.get('q') ?? '' : search;
  const changeSearch = (value: string) => {
    if (!catalogRoute) { setSearch(value); return; }
    setParams(previous => {
      const next = new URLSearchParams(previous);
      if (value) next.set('q', value); else next.delete('q');
      return next;
    }, { replace: true });
  };
  const { data: catalog } = useGetProductsQuery(undefined, { skip: !catalogRoute });
  const clockOffsetMs = catalog
    ? Date.parse(catalog.serverTime) - catalog.clientReceivedAt
    : 0;

  useEffect(() => {
    setHidden(false);
    setSearch('');
    lastScroll.current = Math.max(0, window.scrollY);
  }, [pathname]);

  useEffect(() => {
    const endsAt = Date.parse(EXCLUSIVE_CAMPAIGN_ENDS_AT);
    const update = () => setMeskelActive(Date.now() + clockOffsetMs < endsAt);
    update();
    const timeout = window.setTimeout(
      update,
      Math.max(0, endsAt - (Date.now() + clockOffsetMs) + 50),
    );
    document.addEventListener('visibilitychange', update);
    return () => {
      window.clearTimeout(timeout);
      document.removeEventListener('visibilitychange', update);
    };
  }, [clockOffsetMs]);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const current = Math.max(0, window.scrollY);
      const difference = current - lastScroll.current;

      if (current < 12) {
        setHidden(false);
        lastScroll.current = current;
      } else if (difference > 5) {
        setHidden(true);
        lastScroll.current = current;
      } else if (difference < -5) {
        setHidden(false);
        lastScroll.current = current;
      }
    };

    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  const showThemeRipple = (event: PointerEvent<HTMLButtonElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    setRipple({
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
      id: rippleId.current++,
    });
  };

  return (
    <header className={[hidden ? styles.barHidden : styles.bar, miniApp ? styles.miniApp : ''].filter(Boolean).join(' ')}>
      <div className={styles.inner}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.h1
          key={title}
          className={storefront ? styles.brand : styles.pageTitle}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.16, ease: [0.2, 0, 0, 1] }}
        >
          <Link to="/" className={styles.brandLink} aria-label="YeneShop home">{storefront ? (meskelActive ? <MeskelBrand paused={hidden} /> : <YeneShopLogo />) : <><YeneShopLogo variant="mark" size={28} /><span>{title}</span></>}</Link>
        </motion.h1>
      </AnimatePresence>

      {!miniApp && <form className={styles.desktopSearch} role="search" onSubmit={event => {
        event.preventDefault();
        if (!catalogRoute) navigate(`/${searchValue.trim() ? `?q=${encodeURIComponent(searchValue.trim())}` : ''}#catalog`);
        else document.getElementById('catalog')?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      }}>
        <SearchField query={searchValue} onChange={changeSearch} placeholder="Search tools, templates and digital products" label="Search digital products" flush />
      </form>}
      <div className={styles.actions}>
        {!miniApp && <span className={styles.currency} title="Prices in Ethiopian birr">ETB</span>}
        <NotificationBell />
        <Link to="/seller/workspace" className={styles.themeAction} aria-label="My shop" title="My shop"><Storefront size={20} /></Link>
        <button
          className={styles.themeAction}
          type="button"
          aria-label={isDark ? 'Use light theme' : 'Use dark theme'}
          title={isDark ? 'Use light theme' : 'Use dark theme'}
          onPointerDown={showThemeRipple}
          onClick={() => {
            const next: ThemeMode = isDark ? 'light' : 'dark';
            haptics.select();
            setThemeMode(next);
            setTheme(next);
          }}
        >
          {ripple ? (
            <span
              key={ripple.id}
              className={styles.ripple}
              style={{ left: ripple.x, top: ripple.y }}
              aria-hidden="true"
            />
          ) : null}
          {isDark ? (
            <Sun size={20} weight="fill" />
          ) : (
            <Moon size={20} weight="fill" />
          )}
        </button>
        {!miniApp && <ButtonLink variant="secondary" to="/account" className={styles.accountAction} aria-label={user ? 'My account' : 'Sign in'}><UserCircle size={20} /><span>{user ? 'My account' : 'Sign in'}</span></ButtonLink>}
      </div>
      </div>
      {!miniApp && <div className={styles.navWrap}>
        <nav className={styles.desktopNav} aria-label="Main navigation">
          {pathname === '/' ? <>
            <Link to="/" className={styles.catalogLink} aria-current={!params.get('category') ? 'page' : undefined}>All products</Link>
            <div className={styles.desktopCategories}>
              <CategoryTabs categories={categories} value={params.get('category') ?? ''} ariaLabel="Product categories" onChange={categoryId => navigate(categoryId ? `/?category=${encodeURIComponent(categoryId)}#catalog` : '/#catalog')} />
            </div>
          </> : <>
            <Link to="/">All products</Link>
            {categories.map(category => <Link key={category.id} className={styles.categoryLink} to={`/?category=${encodeURIComponent(category.id)}#catalog`}>{category.name}</Link>)}
          </>}
          <div className={styles.navUtilities}><NavLink to="/discover">Discover</NavLink><NavLink to="/orders">My orders</NavLink><NavLink to="/seller/workspace">Sell on YeneShop</NavLink></div>
        </nav>
      </div>}
    </header>
  );
}
